/**
 * The one HTTP method and status colour map (docs/brand.md → HTTP semantic colours). The
 * `method-*` and `status-*` colours are per-theme tokens in index.css; class names stay
 * literal so Tailwind's scanner finds them.
 *
 * `badge` is token text on a 15% tint of the same token; `fill` is the solid token, for
 * chart series.
 */
export interface HttpColor {
  badge: string;
  fill: string;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';

export type StatusClass = '1xx' | '2xx' | '3xx' | '4xx' | '5xx';

export const METHOD_COLOR: Record<HttpMethod, HttpColor> = {
  GET: { badge: 'bg-method-get/15 text-method-get', fill: 'bg-method-get' },
  POST: { badge: 'bg-method-post/15 text-method-post', fill: 'bg-method-post' },
  PUT: { badge: 'bg-method-put/15 text-method-put', fill: 'bg-method-put' },
  PATCH: { badge: 'bg-method-patch/15 text-method-patch', fill: 'bg-method-patch' },
  DELETE: { badge: 'bg-method-delete/15 text-method-delete', fill: 'bg-method-delete' },
  HEAD: { badge: 'bg-method-head/15 text-method-head', fill: 'bg-method-head' },
  OPTIONS: { badge: 'bg-method-options/15 text-method-options', fill: 'bg-method-options' },
};

export const STATUS_COLOR: Record<StatusClass, HttpColor> = {
  '1xx': { badge: 'bg-status-1xx/15 text-status-1xx', fill: 'bg-status-1xx' },
  '2xx': { badge: 'bg-status-2xx/15 text-status-2xx', fill: 'bg-status-2xx' },
  '3xx': { badge: 'bg-status-3xx/15 text-status-3xx', fill: 'bg-status-3xx' },
  '4xx': { badge: 'bg-status-4xx/15 text-status-4xx', fill: 'bg-status-4xx' },
  '5xx': { badge: 'bg-status-5xx/15 text-status-5xx', fill: 'bg-status-5xx' },
};

/** For a method mockd doesn't know, or a status outside 100–599. */
export const UNKNOWN_HTTP_COLOR: HttpColor = {
  badge: 'bg-base-300 text-base-content',
  fill: 'bg-base-300',
};

export function statusClass(status: number): StatusClass | null {
  if (!Number.isInteger(status) || status < 100 || status > 599) return null;
  return `${Math.floor(status / 100)}xx` as StatusClass;
}

export function methodColor(method: string): HttpColor {
  return METHOD_COLOR[method.toUpperCase() as HttpMethod] ?? UNKNOWN_HTTP_COLOR;
}

export function statusColor(status: number): HttpColor {
  const cls = statusClass(status);
  return cls ? STATUS_COLOR[cls] : UNKNOWN_HTTP_COLOR;
}
